/* ==========================================================================
   HEADER — all header behaviour (loaded with defer from layout/theme.liquid).
   Markup: snippets/header-general-main.liquid and the header-* / mega-*
   snippets it renders. A deferred script runs after parsing and before
   DOMContentLoaded, so the mobile menu's DOMContentLoaded listener still fires.
   ========================================================================== */

/* -------- Mobile menu drawer --------
   The drawer's contents live in <template id="mdm-template"> (snippets/header-mobile.liquid) and are
   cloned into .mobile-menu on the first open. While open: focus starts on the close button, Tab and
   Shift+Tab stay inside the drawer; on close focus returns to the hamburger. */
document.addEventListener("DOMContentLoaded", function () {
  const hamburger = document.getElementById("mobile-hamburger");
  const overlay = document.getElementById("mobile-menu-overlay");
  if (!hamburger || !overlay) return;
  const menu = overlay.querySelector(".mobile-menu");
  const closeBtn = document.getElementById("mobile-menu-close");
  const isOpen = () => overlay.style.display === "block";

  const mountDrawer = () => {
    const tpl = document.getElementById("mdm-template");
    if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
  };

  hamburger.addEventListener("click", function () {
    mountDrawer();
    overlay.style.display = "block";
    document.documentElement.classList.add("menu-drawer-open");
    hamburger.setAttribute("aria-expanded", "true");
    setTimeout(() => {
      menu.classList.add("open");
      closeBtn.focus();
    }, 10);
  });

  closeBtn.addEventListener("click", function () {
    menu.classList.remove("open");
    setTimeout(() => overlay.style.display = "none", 300);
    document.documentElement.classList.remove("menu-drawer-open");
    hamburger.setAttribute("aria-expanded", "false");
    hamburger.focus();
  });

  // Focusable, visible elements inside the drawer (collapsed sections are [hidden], so they drop out)
  const focusables = () => [...menu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter(el => el.getClientRects().length > 0);

  document.addEventListener("keydown", function (e) {
    if (!isOpen()) return;
    if (e.key === "Escape") { closeBtn.click(); return; }
    if (e.key !== "Tab") return;
    const items = focusables();
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !menu.contains(active))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (active === last || !menu.contains(active))) { e.preventDefault(); first.focus(); }
  });

  overlay.addEventListener("click", function (e) {
    if (e.target === overlay) {
      closeBtn.click();
    }
  });
});

/* -------- Support hours: status dot + "Online now" (computed in the browser; pages are cached) -------- */
(() => {
  const host = document.querySelector('[data-hdr-sticky]');
  if (!host) return;
  let cfg = null;
  try { cfg = JSON.parse(host.dataset.hours || ''); } catch (e) { cfg = null; }
  const onlineLabel = host.dataset.onlineLabel || '';
  const hoursLabel = host.dataset.hoursLabel || '';
  const valid = cfg && cfg.tz && Array.isArray(cfg.days) && /^\d\d:\d\d$/.test(cfg.open || '') && /^\d\d:\d\d$/.test(cfg.close || '');
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  function isOpen(now) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: cfg.tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
    const get = t => (parts.find(p => p.type === t) || {}).value || '';
    const day = DAYS.indexOf(get('weekday')) + 1;
    const hm = get('hour') + ':' + get('minute');
    return cfg.days.indexOf(day) !== -1 && hm >= cfg.open && hm < cfg.close;
  }
  function apply() {
    let on = false;
    if (valid) { try { on = isOpen(new Date()); } catch (e) { on = false; } }
    document.querySelectorAll('[data-hdr-expert]').forEach(el => el.classList.toggle('is-online', on));
    document.querySelectorAll('[data-hdr-status]').forEach(el => { el.textContent = on && onlineLabel ? onlineLabel : hoursLabel; });
  }
  apply();
  setInterval(apply, 5 * 60 * 1000);
})();

/* -------- Sticky compact header: shows on scroll-up past the header, hides on scroll-down -------- */
(() => {
  const el = document.querySelector('[data-hdr-sticky]');
  const spacer = el && el.previousElementSibling;
  if (!el || !spacer || !spacer.classList.contains('hdr-spacer')) return;
  const root = document.documentElement;
  let stuck = false, shown = false, lastY = window.scrollY, upBy = 0, ticking = false, normalH = 0, normalTop = 0;

  const measure = () => { if (stuck) return; const r = el.getBoundingClientRect(); normalH = r.height; normalTop = r.top + window.scrollY; };
  // never hide while the drawer or a mega panel is open, the header search has focus, or the search suggestions are open
  const keepVisible = () =>
    root.classList.contains('menu-drawer-open') || root.classList.contains('mega-panel-open') ||
    (document.activeElement && el.contains(document.activeElement) && document.activeElement.matches('input[name="q"]')) ||
    root.classList.contains('hdr-search-open');

  function stick() {
    stuck = true; shown = false;
    spacer.style.height = normalH + 'px';
    el.classList.remove('is-shown', 'is-anim');
    el.classList.add('is-stuck');
    requestAnimationFrame(() => el.classList.add('is-anim'));
  }
  function unstick() {
    stuck = false; shown = false;
    el.classList.remove('is-stuck', 'is-shown', 'is-anim');
    spacer.style.height = '0px';
  }
  function show(on) {
    if (shown === on) return;
    shown = on;
    el.classList.toggle('is-shown', on);
    if (on) root.style.setProperty('--hdr-compact-h', el.offsetHeight + 'px');
  }
  function update() {
    ticking = false;
    const y = window.scrollY, dy = y - lastY;
    lastY = y;
    if (y < normalTop + normalH) { if (stuck && !keepVisible()) unstick(); upBy = 0; return; }
    if (!stuck) { stick(); if (root.classList.contains('hdr-search-open')) show(true); } // suggestions stay attached to a visible header
    if (dy < 0) { upBy -= dy; if (upBy > 8) show(true); }
    else if (dy > 0) { upBy = 0; if (!keepVisible()) show(false); }
  }
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  window.addEventListener('resize', () => { if (stuck) unstick(); measure(); update(); }, { passive: true });
  window.addEventListener('load', measure);
  window.__hdrMeasure = measure; // re-measure without a resize (a resize unsticks the header)
  measure();
})();

/* -------- Product pages: the search icon opens the mobile search row (it stays open for the page view) -------- */
(() => {
  const btn = document.querySelector('[data-mobile-search-open]');
  const header = btn && btn.closest('.mobile-header');
  if (!header) return;
  btn.addEventListener('click', () => {
    header.classList.add('is-search-open');
    btn.setAttribute('aria-expanded', 'true');
    const input = document.getElementById('MobileHeaderSearch');
    if (input) input.focus(); // inside the tap, so the keyboard comes up and the suggestions sheet opens
    if (window.__hdrMeasure) window.__hdrMeasure();
  });
})();

/* -------- Mega menu controller -------- */
(() => {
  const nav = document.querySelector('.mm');
  if (!nav || nav.dataset.mmInit) return;
  nav.dataset.mmInit = '1';

  const header = nav.closest('.maingrid-header') || nav;
  const backdrop = nav.querySelector('.mm-backdrop');
  const tabs = [...nav.querySelectorAll('.mm-tab[data-panel]')];
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const OPEN_DELAY = 100, LEAVE_DELAY = 160, ANIM_OUT = reduceMotion ? 0 : 180;

  // Suppress hover auto-open for a moment after a click-navigation
  const SUPPRESS_KEY = 'mega_hover_block_until', SUPPRESS_MS = 1800;
  const hoverBlocked = () => { try { return Number(sessionStorage.getItem(SUPPRESS_KEY) || 0) > Date.now(); } catch (_) { return false; } };
  const suppressHover = () => { try { sessionStorage.setItem(SUPPRESS_KEY, String(Date.now() + SUPPRESS_MS)); } catch (_) {} };

  // Hover only on real pointers, and only after the mouse has moved (no open on page load)
  const canHover = matchMedia('(any-hover: hover) and (any-pointer: fine)').matches;
  let hoverArmed = false;
  if (canHover) window.addEventListener('pointermove', e => { if (e.pointerType === 'mouse') hoverArmed = true; }, { once: true, passive: true });

  let current = null;

  // Panel images are lazy inside display:none panels, so they would only start loading when a panel opens.
  // When a tab is pointed at (before the open delay) or focused, switch that panel's images to eager, once.
  const loadPanelImages = panel => {
    if (!panel || panel.dataset.mmImgs) return;
    panel.dataset.mmImgs = '1';
    panel.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
  };
  const panelOf = tab => document.getElementById(tab.dataset.panel);

  function showBackdrop(on) {
    // lets theme CSS hide floating widgets (chat, Klaviyo teaser) while a panel is open
    document.documentElement.classList.toggle('mega-panel-open', on);
    if (!backdrop) return;
    if (on) {
      backdrop.style.top = Math.max(0, header.getBoundingClientRect().bottom) + 'px';
      backdrop.hidden = false;
      requestAnimationFrame(() => backdrop.classList.add('is-on'));
    } else {
      backdrop.classList.remove('is-on');
      backdrop.hidden = true;
    }
  }

  function open(tab) {
    const panel = panelOf(tab);
    if (!panel) return;
    clearTimeout(tab._closeT);
    if (current && current !== tab) close(current);
    clearTimeout(panel._unmountT);
    panel.classList.remove('is-closing');
    panel.classList.add('is-mounted');
    void panel.offsetHeight; // start the transition from the closed state
    panel.classList.add('is-open');
    tab.classList.add('is-active');
    tab.setAttribute('aria-expanded', 'true');
    current = tab;
    showBackdrop(true);
  }

  function close(tab) {
    const panel = panelOf(tab);
    clearTimeout(tab._closeT);
    clearTimeout(tab._hoverT);
    tab.classList.remove('is-active');
    tab.setAttribute('aria-expanded', 'false');
    if (current === tab) { current = null; showBackdrop(false); }
    if (!panel || !panel.classList.contains('is-mounted')) return;
    panel.classList.remove('is-open');
    panel.classList.add('is-closing');
    panel._unmountT = setTimeout(() => panel.classList.remove('is-closing', 'is-mounted'), ANIM_OUT);
  }

  const scheduleClose = tab => { clearTimeout(tab._closeT); tab._closeT = setTimeout(() => close(tab), LEAVE_DELAY); };
  const firstLink = panel => panel.querySelector('a[href], button:not([disabled])');

  tabs.forEach(tab => {
    const panel = panelOf(tab);
    const item = tab.closest('.mm-item');

    // Click navigates to the tab's page; block hover-open briefly on the next page
    tab.addEventListener('click', suppressHover, { capture: true });

    // Load this panel's images on mouse pointerenter (ahead of the hover-open delay) or keyboard focus
    tab.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') loadPanelImages(panel); });
    tab.addEventListener('focus', () => loadPanelImages(panel));

    // Keyboard: ArrowDown / Space opens and moves into the panel; Enter keeps navigating
    tab.addEventListener('keydown', e => {
      if (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        open(tab);
        const target = firstLink(panel);
        if (target) target.focus();
      }
    });

    // Leaving tab + panel with the keyboard closes it
    item.addEventListener('focusout', e => {
      if (current === tab && !item.contains(e.relatedTarget)) close(tab);
    });

    if (canHover) {
      tab.addEventListener('pointerenter', e => {
        if (e.pointerType !== 'mouse' || !hoverArmed || hoverBlocked()) return;
        clearTimeout(tab._closeT);
        clearTimeout(tab._hoverT);
        tab._hoverT = setTimeout(() => open(tab), OPEN_DELAY);
      });
      tab.addEventListener('pointerleave', e => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(tab._hoverT);
        if (current === tab) scheduleClose(tab);
      });
      panel.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') clearTimeout(tab._closeT); });
      panel.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse' && current === tab) scheduleClose(tab); });
    }
  });

  // Escape closes and returns focus to the tab
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape' || !current) return;
    const tab = current;
    const hadFocus = tab.closest('.mm-item').contains(document.activeElement);
    close(tab);
    if (hadFocus) tab.focus();
  });

  if (backdrop) backdrop.addEventListener('click', () => { if (current) close(current); });

  // Below the desktop breakpoint the header is hidden; never leave a panel open
  matchMedia('(max-width: 1023px)').addEventListener('change', e => { if (e.matches && current) close(current); });
})();

/* -------- Drawer accordion (snippets/header-drawer-menu.liquid) --------
   The drawer's markup is cloned from <template id="mdm-template"> on first open,
   so this is one delegated listener on the overlay, which is always in the page. */
(() => {
  const root = document.getElementById('mobile-menu-overlay');
  if (!root || root.dataset.mdmInit) return;
  root.dataset.mdmInit = '1';
  // Accordion: one section open at a time
  root.addEventListener('click', e => {
    const btn = e.target.closest('.mdm__toggle');
    if (!btn) return;
    const willOpen = btn.getAttribute('aria-expanded') !== 'true';
    root.querySelectorAll('.mdm__toggle[aria-expanded="true"]').forEach(b => {
      if (b === btn) return;
      b.setAttribute('aria-expanded', 'false');
      const body = document.getElementById(b.getAttribute('aria-controls'));
      if (body) body.hidden = true;
    });
    btn.setAttribute('aria-expanded', String(willOpen));
    const body = document.getElementById(btn.getAttribute('aria-controls'));
    if (body) body.hidden = !willOpen;
    // Drawer images are lazy; load only the section being expanded
    if (body && willOpen) body.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
  });
})();

(() => {
  /* -------- HEADER CLICK TRACKING (no markup) --------
     Sends GA4 events through gtag (if present) with transport_type beacon, and the same
     event name to Microsoft Clarity (if present). One delegated click listener and one
     submit listener, both in the capture phase so other handlers can't swallow them.
     Events: search, search_suggestion_click (link_type: query | collection | product | chip | recent | tile | expert),
     search_no_results, phone_click, header_click, nav_click.
     nav_click link_type comes from the class names in header-nav / header-drawer-menu
     and the mega-* helper snippets:
     mm-card → card, mm-brand → brand, mm-prod → best_seller, mm-pkg → package, mm-pill → chip, mm-guide → guide,
     links in .mm-services → service, mm-feat__cta / mm-btn → hero,
     mm-foot__all / mdm__all / mm-more → all-link, mdm__util → utility,
     mm-tab → tab, anything else (column lists, mdm__row) → text */
  if (window.__hdrTracking) return;
  window.__hdrTracking = true;

  const send = (name, params) => {
    try { if (typeof gtag === 'function') gtag('event', name, Object.assign({ transport_type: 'beacon' }, params)); } catch (e) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) {}
  };
  const isSticky = () => { const s = document.querySelector('[data-hdr-sticky]'); return !!(s && s.classList.contains('is-stuck')); };
  const text = el => (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  const href = a => a.getAttribute('href') || '';
  const term = v => (v || '').trim().slice(0, 100);

  const linkType = a => {
    if (a.matches('.mm-card')) return 'card';
    if (a.matches('.mm-brand')) return 'brand';
    if (a.matches('.mm-prod')) return 'best_seller';
    if (a.matches('.mm-pkg')) return 'package';
    if (a.matches('.mm-pill')) return 'chip';
    if (a.matches('.mm-guide')) return 'guide';
    if (a.closest('.mm-services')) return 'service';
    if (a.matches('.mm-feat__cta, .mm-btn')) return 'hero';
    if (a.matches('.mm-foot__all, .mdm__all, .mm-more')) return 'all-link';
    if (a.matches('.mdm__util')) return 'utility';
    if (a.matches('.mm-tab')) return 'tab';
    return 'text';
  };
  // the tab label that owns the panel or drawer section around the link
  const panelLabel = a => {
    const panel = a.closest('.mm-panel');
    if (panel) { const tab = document.querySelector('[aria-controls="' + panel.id + '"]'); return tab ? text(tab) : ''; }
    const body = a.closest('.mdm__body');
    if (body) { const toggle = document.querySelector('[aria-controls="' + body.id + '"]'); return toggle ? text(toggle) : ''; }
    if (a.matches('.mm-tab, .mdm__row')) return text(a);
    return '';
  };

  // Search: the header fields and the suggestions panel (#HeaderSearchPanel, assets/header-search.js).
  // On phones the panel is a full-screen sheet with its own field (inside the panel), so a field
  // or form in the mobile header or in the panel counts as mobile. Remember the last term typed.
  const isHeaderField = t => t && t.name === 'q' && t.closest && t.closest('[data-hdr-sticky]');
  const isMobile = el => !!el.closest('.mobile-header, #HeaderSearchPanel');
  const where = el => (isMobile(el) ? 'header_mobile' : 'header_desktop') + (isSticky() ? '_sticky' : '');
  let lastTerm = '', lastSearchAt = 0;
  const noResultsSent = new Set();
  document.addEventListener('input', e => {
    if (isHeaderField(e.target)) lastTerm = e.target.value;
  }, true);
  const currentTerm = () => {
    const a = document.activeElement;
    if (isHeaderField(a) && a.value) return a.value;
    return lastTerm;
  };
  const sendSearch = (value, location) => {
    const now = Date.now();
    if (now - lastSearchAt < 1000) return; // one search per action
    lastSearchAt = now;
    send('search', { search_term: term(value), search_location: location });
  };

  document.addEventListener('submit', e => {
    try {
      const form = e.target;
      if (!form.closest || !form.closest('[data-hdr-sticky]')) return;
      const q = form.querySelector('input[name="q"]');
      sendSearch((q && q.value) || currentTerm(), where(form));
    } catch (err) {}
  }, true);

  // The no-results state rendered in the panel (once per term)
  document.addEventListener('hdr-search:no-results', e => {
    try {
      const t = term(e.detail && e.detail.term);
      if (!t || noResultsSent.has(t.toLowerCase())) return;
      noResultsSent.add(t.toLowerCase());
      send('search_no_results', { search_term: t });
    } catch (err) {}
  });

  document.addEventListener('click', e => {
    try {
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;

      // Expert card (panel no-results and /search no-results) and the panel's help line
      if (href(a).indexOf('tel:') === 0 && a.closest('#HeaderSearchPanel, [data-search-expert]')) {
        send('phone_click', { link_area: 'search' });
        return;
      }
      if (a.closest('#HeaderSearchPanel')) {
        if (a.matches('[data-hs-all]')) { sendSearch(currentTerm(), where(a)); return; }
        const type = a.getAttribute('data-hs-type');
        if (!type) return;
        const label = a.querySelector('[data-hs-label]') || a;
        send('search_suggestion_click', { search_term: term(currentTerm()), link_text: text(label), link_type: type, link_url: href(a) });
        return;
      }

      const inAnnc = a.closest('.annc');
      const inPanel = a.closest('.mm-panel');
      const inDrawer = a.closest('.mobile-menu');
      const inHeader = a.closest('[data-hdr-sticky]');

      if (href(a).indexOf('tel:') === 0) {
        const area = inAnnc ? 'announcement' : inPanel ? 'mega_panel' : inDrawer ? 'drawer' : inHeader ? (isSticky() ? 'header_sticky' : 'header') : '';
        if (area) send('phone_click', { link_area: area });
        return;
      }

      if (inPanel || a.closest('.mdm') || a.matches('.mm-tab')) {
        send('nav_click', { panel: panelLabel(a), link_type: linkType(a), link_text: text(a), link_url: href(a), device: a.closest('.mobile-header, .mobile-menu-overlay') ? 'mobile' : 'desktop' });
        return;
      }

      let area = '';
      if (inAnnc) area = a.matches('.annc-util') ? 'utility' : a.matches('.annc-link') ? 'announcement' : '';
      else if (a.closest('.maingrid-header-logo, .mobile-header-logo')) area = 'logo';
      else if (a.matches('.hdr-account') || a.closest('.mobile-menu-header')) area = 'account';
      else if (a.matches('.cart-link, .mobile-cart-link')) area = 'cart';
      else if (a.matches('.mm-quote')) area = 'quote_cta';
      if (area) send('header_click', { link_area: area, link_text: text(a), link_url: href(a) });
    } catch (err) {}
  }, true);
})();
